import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Drawer,
  Pagination,
  Skeleton,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { DeepViewEndPointLogic } from "../../../../../logic/DeepView/DeepViewEndPointLogic";
import { useBizDevelopmentDashboard } from "../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import {
  PERC_SUFFIX,
  NO_COMPARISON_KEYS,
  COMPUTED_PERC,
  NATIVE_PERCENTAGE_KEYS,
  ROUND_KEYS,
  getBadgeStyle,
  getCurrencySymbol,
  getCurrencySymbolFromCode,
  getDateRangeForTimeframe,
  ORG_CURRENCY_INCOME_KEYS,
  DEEP_VIEW_KEYS,
  formatValue,
  buildColumns,
  sortRows,
  calcDiffPercent,
  OutputMetaField,
  TableColumn,
} from "../../helpers/bizDevelopmentDashboardHelpers";
import DatacollectionLastRunDetailsView from "../../../../DeepView/DatacollectionLastRunDetailsView";
import {
  DEEP_VIEW_VALUE_CLASS,
  deepViewCellSx,
} from "../../../../DeepView/deepViewCellStyles";

interface Props {
  outputMetadata: Record<string, OutputMetaField>;
  queryOutput: Record<string, any>[];
  isLoading?: boolean;
  comparisonQueryOutput?: Record<string, any>[];
  isComparisonLoading?: boolean;
  isComparisonActive?: boolean;
}

const SKELETON_ROWS = 8;
const SKELETON_COLS = 10;

// NOTE: Custom override to append '%' symbol to ROI column, which is not mentioned in default docs.
const formatCellValue = (
  col: TableColumn,
  row: Record<string, any>,
  value: any
) => {
  if (value === null || value === undefined) return "N/A";
  // ID fields are plain identifiers — no comma separator
  if (col.key.endsWith("_id")) return String(value);
  const currencySymbol = ORG_CURRENCY_INCOME_KEYS.has(col.key)
    ? getCurrencySymbolFromCode(row.deal_currency)
    : getCurrencySymbol(col.key);
  // Currency fields and explicit ROUND_KEYS are integer values — round before formatting so thousand separator applies correctly
  const normalized =
    (currencySymbol != null || ROUND_KEYS.has(col.key)) && !isNaN(Number(value))
      ? Math.round(Number(value))
      : value;
  const valStr = formatValue(normalized);
  if (valStr === "N/A") return valStr;
  if (NATIVE_PERCENTAGE_KEYS.has(col.key)) {
    return `${valStr}%`;
  }
  if (col.key.toLowerCase() === "roi") {
    return `${parseFloat((Number(valStr) * 100).toFixed(1))}%`;
  }
  return currencySymbol ? `${currencySymbol}${valStr}` : valStr;
};

const PerformanceTable = ({
  outputMetadata,
  queryOutput,
  isLoading = false,
  comparisonQueryOutput = [],
  isComparisonLoading = false,
  isComparisonActive = false,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const oauthTokenHeader = userData?.data?.oauth_token;
  // Deep View is gated: without this permission the cells stay plain text
  const hasDeepViewPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "businesse_advisor",
    "view_advisor_maps"
  );
  const { getSitesFromParams, getTimeframeFromParams } =
    useBizDevelopmentDashboard();

  const isMtd = searchParams.get("timeframePerformance") === "mtd";

  const page = parseInt(searchParams.get("pagePerformance") ?? "0", 10);
  const rowsPerPage = parseInt(
    searchParams.get("rowsPerPagePerformance") ?? "10",
    10
  );
  // Kept separate from `deepViewCell` so the drawer keeps its content while closing
  const [isDeepViewOpen, setIsDeepViewOpen] = React.useState(false);
  const [deepViewCell, setDeepViewCell] = React.useState<{
    columnLabel: string;
    columnKey: string;
    value: string | number;
  } | null>(null);

  // The date range the table is actually showing. `getDateRangeForTimeframe`
  // has no "custom" case and falls back to yesterday, so a custom range has to
  // be read from the URL params the timeframe control writes.
  const getActiveDateRange = () => {
    const timeframe = getTimeframeFromParams();
    const customStart = searchParams.get("customStartPerformance");
    const customEnd = searchParams.get("customEndPerformance");

    if (timeframe === "custom" && customStart && customEnd) {
      return { startDate: customStart, endDate: customEnd };
    }
    return getDateRangeForTimeframe(timeframe);
  };

  const openDeepView = (col: TableColumn, row: Record<string, any>) => {
    if (!hasDeepViewPermission) return;
    setDeepViewCell({
      columnLabel: col.title,
      columnKey: col.key,
      value: formatCellValue(col, row, row[col.key]),
    });
    setIsDeepViewOpen(true);

    const { startDate, endDate } = getActiveDateRange();
    DeepViewEndPointLogic.getDeepViewData(oauthTokenHeader, {
      brandId: row?.brand_id,
      startDate,
      endDate,
      websiteSharedUids: getSitesFromParams(),
    });
  };

  const sortKey = searchParams.get("sortKeyPerformance") ?? "";
  const sortDir = (searchParams.get("sortDirPerformance") ?? "asc") as
    | "asc"
    | "desc";

  const setParam = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      Object.entries(updates).forEach(([k, v]) => prev.set(k, v));
      return prev;
    });
  };

  const handleChangePage = (_: any, newPage: number) =>
    setParam({ pagePerformance: String(newPage) });

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) =>
    setParam({ rowsPerPagePerformance: e.target.value, pagePerformance: "0" });

  const handleSort = (key: string) => {
    const newDir = sortKey === key && sortDir === "asc" ? "desc" : "asc";
    setParam({
      sortKeyPerformance: key,
      sortDirPerformance: newDir,
      pagePerformance: "0",
    });
  };

  const columns = React.useMemo(
    () => buildColumns(outputMetadata, isMtd),
    [outputMetadata, isMtd]
  );

  const sortedRows = React.useMemo(
    () => sortRows(queryOutput, sortKey, sortDir),
    [queryOutput, sortKey, sortDir]
  );

  const renderDiff = (primary: any, comp: any) => {
    const pct = calcDiffPercent(primary, comp);
    if (pct === null) return null;
    const label = `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
    if (pct > 0)
      return (
        <Box display="flex" alignItems="center" gap={0.1}>
          <Typography
            sx={{ color: "#2E7D32", fontSize: "10px", fontWeight: 500 }}
          >
            {label}
          </Typography>
          <ArrowDropUpIcon sx={{ color: "#2E7D32", fontSize: "18px" }} />
        </Box>
      );
    if (pct < 0)
      return (
        <Box display="flex" alignItems="center" gap={0.1}>
          <Typography
            sx={{ color: "#D32F2F", fontSize: "10px", fontWeight: 500 }}
          >
            {label}
          </Typography>
          <ArrowDropDownIcon sx={{ color: "#D32F2F", fontSize: "18px" }} />
        </Box>
      );
    return (
      <Box display="flex" alignItems="center" gap={0.1}>
        <Typography
          sx={{ color: "#00000099", fontSize: "10px", fontWeight: 500 }}
        >
          0.00%
        </Typography>
        <DragHandleIcon sx={{ color: "#00000099", fontSize: "18px" }} />
      </Box>
    );
  };

  const renderCell = (
    col: TableColumn,
    row: Record<string, any>,
    compRow: Record<string, any> | undefined,
    compLoading: boolean,
    isComparisonActive: boolean
  ) => {
    const raw = row[col.key];
    const percRaw = row[col.key + PERC_SUFFIX];
    const skipComparison = NO_COMPARISON_KEYS.has(col.key);
    const compRaw = !skipComparison ? compRow?.[col.key] : undefined;
    const compPercRaw = !skipComparison
      ? compRow?.[col.key + PERC_SUFFIX]
      : undefined;
    const hasComp = !skipComparison && !!compRow;
    const isMissingComp =
      !skipComparison && isComparisonActive && !compRow && !compLoading;
    const fmtVal = (v: any) => formatCellValue(col, row, v);

    if (col.hasPerc) {
      const percKey = col.key + PERC_SUFFIX;
      const percNum =
        percRaw != null ? (Number(percRaw) * 100).toFixed(2) : null;
      const style =
        percNum != null ? getBadgeStyle(percKey, Number(percNum), isMtd) : null;
      const compPercNum =
        compPercRaw != null ? (Number(compPercRaw) * 100).toFixed(2) : null;
      const compStyle =
        compPercNum != null
          ? getBadgeStyle(percKey, Number(compPercNum), isMtd)
          : null;

      return (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize="inherit" className={DEEP_VIEW_VALUE_CLASS}>
                {fmtVal(raw)}
              </Typography>
              {percNum != null && style && (
                <Box
                  sx={{
                    backgroundColor: style.bg,
                    color: style.color,
                    border: `1px solid ${style.border}`,
                    borderRadius: "4px",
                    px: "4px",
                    py: "0px",
                    fontSize: "10px",
                    fontWeight: 500,
                  }}
                >
                  {percNum}%
                </Box>
              )}
            </Box>
            {!skipComparison && compLoading && (
              <Skeleton variant="text" width={60} />
            )}
            {hasComp && !compLoading && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontSize="inherit" color="#00000061">
                  {fmtVal(compRaw)}
                </Typography>
                {compPercNum != null && compStyle && (
                  <Box
                    sx={{
                      backgroundColor: "#0000001F",
                      color: "#00000061",
                      border: "1px solid #00000061",
                      borderRadius: "4px",
                      px: "4px",
                      py: "0px",
                      fontSize: "10px",
                      fontWeight: 500,
                    }}
                  >
                    {compPercNum}%
                  </Box>
                )}
              </Box>
            )}
            {isMissingComp && (
              <Typography fontSize="inherit" color="#00000061">
                No Data
              </Typography>
            )}
          </Box>
          {hasComp && !compLoading && renderDiff(raw, compRaw)}
        </Box>
      );
    }

    if (col.isPercentageValue) {
      const toDisplay = (v: any) =>
        v != null
          ? NATIVE_PERCENTAGE_KEYS.has(col.key)
            ? Number(v).toFixed(2)
            : (Number(v) * 100).toFixed(2)
          : null;
      const pctNum = toDisplay(raw);
      const style =
        pctNum != null ? getBadgeStyle(col.key, Number(pctNum), isMtd) : null;
      const compPctNum = toDisplay(compRaw);
      const compStyle =
        compPctNum != null
          ? getBadgeStyle(col.key, Number(compPctNum), isMtd)
          : null;
      return (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Box display="flex" alignItems="center" gap={1}>
            {pctNum != null && style ? (
              <Box
                sx={{
                  backgroundColor: style.bg,
                  color: style.color,
                  border: `1px solid ${style.border}`,
                  borderRadius: "4px",
                  px: "4px",
                  py: "0px",
                  fontSize: "10px",
                  fontWeight: 500,
                }}
              >
                {pctNum}%
              </Box>
            ) : (
              <Typography fontSize="inherit">N/A</Typography>
            )}
            {!skipComparison && compLoading && (
              <Skeleton variant="text" width={40} />
            )}
            {hasComp && !compLoading && compPctNum != null && compStyle && (
              <Box
                sx={{
                  backgroundColor: "#0000001F",
                  color: "#00000061",
                  border: "1px solid #00000061",
                  borderRadius: "4px",
                  px: "4px",
                  py: "0px",
                  fontSize: "10px",
                  fontWeight: 500,
                }}
              >
                {compPctNum}%
              </Box>
            )}
            {isMissingComp && (
              <Typography fontSize="inherit" color="#00000061">
                No Data
              </Typography>
            )}
          </Box>
          {hasComp && !compLoading && renderDiff(raw, compRaw)}
        </Box>
      );
    }

    const computedPercFn = COMPUTED_PERC[col.key];
    if (computedPercFn) {
      const pct = computedPercFn(row);
      const pctStr = pct != null ? pct.toFixed(2) : null;
      const style =
        pctStr != null ? getBadgeStyle(col.key, Number(pctStr), isMtd) : null;
      const compPct = compRow ? computedPercFn(compRow) : null;
      const compPctStr = compPct != null ? compPct.toFixed(2) : null;
      const compStyle =
        compPctStr != null
          ? getBadgeStyle(col.key, Number(compPctStr), isMtd)
          : null;

      return (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize="inherit" className={DEEP_VIEW_VALUE_CLASS}>
                {fmtVal(raw)}
              </Typography>
              {pctStr != null && style && (
                <Box
                  sx={{
                    backgroundColor: style.bg,
                    color: style.color,
                    border: `1px solid ${style.border}`,
                    borderRadius: "4px",
                    px: "4px",
                    py: "0px",
                    fontSize: "10px",
                    fontWeight: 500,
                  }}
                >
                  {pctStr}%
                </Box>
              )}
            </Box>
            {!skipComparison && compLoading && (
              <Skeleton variant="text" width={60} />
            )}
            {hasComp && !compLoading && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontSize="inherit" color="#00000061">
                  {fmtVal(compRaw)}
                </Typography>
                {compPctStr != null && compStyle && (
                  <Box
                    sx={{
                      backgroundColor: "#0000001F",
                      color: "#00000061",
                      border: "1px solid #00000061",
                      borderRadius: "4px",
                      px: "4px",
                      py: "0px",
                      fontSize: "10px",
                      fontWeight: 500,
                    }}
                  >
                    {compPctStr}%
                  </Box>
                )}
              </Box>
            )}
            {isMissingComp && (
              <Typography fontSize="inherit" color="#00000061">
                No Data
              </Typography>
            )}
          </Box>
          {hasComp && !compLoading && renderDiff(raw, compRaw)}
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={0.25}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontSize="inherit" className={DEEP_VIEW_VALUE_CLASS}>
            {fmtVal(raw)}
          </Typography>
          {!skipComparison && compLoading && (
            <Skeleton variant="text" width={50} />
          )}
          {hasComp && !compLoading && (
            <Typography fontSize="inherit" color="#00000061">
              {fmtVal(compRaw)}
            </Typography>
          )}
          {isMissingComp && (
            <Typography fontSize="inherit" color="#00000061">
              No Data
            </Typography>
          )}
        </Box>
        {hasComp && !compLoading && renderDiff(raw, compRaw)}
      </Box>
    );
  };

  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (isLoading) {
    return (
      <TableContainer>
        <Table size="small" sx={{ minWidth: "1800px", whiteSpace: "nowrap" }}>
          <TableHead>
            <TableRow>
              {Array.from({ length: SKELETON_COLS }).map((_, i) => (
                <TableCell key={i}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
              <TableRow key={rowIdx}>
                {Array.from({ length: SKELETON_COLS }).map((_, colIdx) => (
                  <TableCell key={colIdx}>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer sx={{ overflowX: "auto", minHeight: "100px", py: 2 }}>
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: "1800px", whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} align="left">
                  <TableSortLabel
                    active={sortKey === col.key}
                    direction={sortKey === col.key ? sortDir : "asc"}
                    onClick={() => handleSort(col.key)}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {col.title}
                      {col.tooltip !== undefined && (
                        <Tooltip title={col.tooltip} arrow placement="top">
                          <InfoOutlinedIcon
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              fontSize: "14px",
                              color: "rgba(0,0,0,0.38)",
                              verticalAlign: "middle",
                              flexShrink: 0,
                              "&:hover": { color: "rgba(0,0,0,0.6)" },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length || 1}
                  align="center"
                  sx={{ py: 6, color: "rgba(0,0,0,0.4)", fontSize: "14px" }}
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
            {paginatedRows.map((row, rowIdx) => {
              const compRow = comparisonQueryOutput.find(
                (r) => r.brand_id === row.brand_id
              );
              return (
                <TableRow
                  key={rowIdx}
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                    },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align="left">
                      {hasDeepViewPermission && DEEP_VIEW_KEYS.has(col.key) ? (
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => openDeepView(col, row)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openDeepView(col, row);
                            }
                          }}
                          sx={deepViewCellSx}
                        >
                          {renderCell(
                            col,
                            row,
                            compRow,
                            isComparisonLoading,
                            isComparisonActive
                          )}
                        </Box>
                      ) : (
                        renderCell(
                          col,
                          row,
                          compRow,
                          isComparisonLoading,
                          isComparisonActive
                        )
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        sx={{ position: "relative" }}
        alignItems="center"
      >
        <Stack direction="row" alignItems="center">
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={queryOutput.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{ displayedRows: "hidden-displayed-rows" }}
            sx={{
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-input": { marginRight: "15px" },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={queryOutput.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                { display: "none" },
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-toolbar": { paddingLeft: "0px" },
            }}
          />
        </Stack>

        <Pagination
          count={Math.ceil(queryOutput.length / rowsPerPage)}
          page={page + 1}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            ".css-wjh20t-MuiPagination-ul": { justifyContent: "center" },
          }}
          onChange={(_, value) => handleChangePage(null, value - 1)}
        />

        <Box />
      </Stack>

      <Drawer
        sx={{ zIndex: 9999 }}
        anchor="right"
        open={isDeepViewOpen}
        onClose={() => setIsDeepViewOpen(false)}
      >
        <Box sx={{ width: 600 }} role="presentation">
          <DatacollectionLastRunDetailsView
            columnLabel={deepViewCell?.columnLabel}
            columnKey={deepViewCell?.columnKey}
            value={deepViewCell?.value}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default PerformanceTable;
